import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Send as SendIcon,
  Psychology as InsightIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as SuggestionIcon,
  ContentCopy as CopyIcon,
  TrendingUp as TrendIcon,
  Assessment as AnalysisIcon,
  Security as ComplianceIcon,
  BusinessCenter as OperationalIcon,
} from '@mui/icons-material';
import { ReportQuery, AIQueryResponse } from '../../services/reportingService';
import reportingService from '../../services/reportingService';
import { useAuth } from '../../contexts/AuthContext';

export interface AIQuerySuggestion {
  query: string;
  description: string;
  category: 'financial' | 'operational' | 'compliance' | 'insights';
  complexity: 'simple' | 'intermediate' | 'advanced';
}
interface AIQueryAssistantProps {
  onQueryGenerated: (query: ReportQuery, explanation: string) => void;
  onError: (error: string) => void;
  suggestions: AIQuerySuggestion[];
  onLoadSuggestions: () => void;
}

const AIQueryAssistant: React.FC<AIQueryAssistantProps> = ({
  onQueryGenerated,
  onError,
  suggestions,
  onLoadSuggestions,
}) => {
  const [naturalQuery, setNaturalQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState<AIQueryResponse | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const { user } = useAuth();

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'financial':
        return <TrendIcon color="success" />;
      case 'operational':
        return <OperationalIcon color="primary" />;
      case 'compliance':
        return <ComplianceIcon color="warning" />;
      case 'insights':
        return <AnalysisIcon color="info" />;
      default:
        return <SuggestionIcon />;
    }
  };

  const getComplexityColor = (complexity: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (complexity) {
      case 'simple':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleSubmit = async () => {
    if (!naturalQuery.trim()) {
      onError('Please enter a query to convert');
      return;
    }

    setLoading(true);
    try {
      const aiResponse = await reportingService.convertNaturalLanguageQuery({
        naturalLanguageQuery: naturalQuery,
        context: {
          userId: user?.id,
        },
      });
      setLastResponse(aiResponse);
      onQueryGenerated(aiResponse.structuredQuery, aiResponse.explanation);
      setNaturalQuery(''); // Clear input after successful conversion
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to convert query');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: AIQuerySuggestion) => {
    setNaturalQuery(suggestion.query);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <AIIcon color="primary" />
          <Typography variant="h6">
            AI Query Assistant
          </Typography>
        </Box>

        {/* Natural Language Input */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Describe what you want to analyze"
            placeholder="e.g., Show me all pending loan applications from tech companies, or Which industries have the highest approval rates?"
            value={naturalQuery}
            onChange={(e) => setNaturalQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
          />
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSubmit}
              disabled={loading || !naturalQuery.trim()}
            >
              {loading ? 'Converting...' : 'Convert to Query'}
            </Button>
          </Box>
        </Box>

        {/* Last Response */}
        {lastResponse && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="success" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Query Generated (Confidence: {Math.round(lastResponse.confidence * 100)}%)
                </Typography>
                <Typography variant="body2">
                  {lastResponse.explanation}
                </Typography>
              </Box>
            </Alert>

            {lastResponse.suggestions.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Alternative Query Ideas ({lastResponse.suggestions.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {lastResponse.suggestions.map((suggestion: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <SuggestionIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={suggestion}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                        <Tooltip title="Use this suggestion">
                          <IconButton
                            size="small"
                            onClick={() => setNaturalQuery(suggestion)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}

        {/* Query Suggestions */}
        <Accordion expanded={showSuggestions} onChange={() => setShowSuggestions(!showSuggestions)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <InsightIcon color="primary" />
              <Typography variant="subtitle1">
                Query Suggestions ({suggestions.length})
              </Typography>
              {suggestions.length === 0 && (
                <Button
                  size="small"
                  onClick={onLoadSuggestions}
                  sx={{ ml: 1 }}
                >
                  Load Suggestions
                </Button>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {suggestions.length > 0 ? (
              <List>
                {suggestions.map((suggestion, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                    <ListItemIcon>
                      {getCategoryIcon(suggestion.category)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" component="span">
                            {suggestion.query}
                          </Typography>
                          <Chip
                            label={suggestion.complexity}
                            size="small"
                            color={getComplexityColor(suggestion.complexity)}
                            variant="outlined"
                          />
                          <Chip
                            label={suggestion.category}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={suggestion.description}
                    />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Click "Load Suggestions" to see AI-powered query examples
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Usage Tips */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            💡 Tips for better results:
          </Typography>
          <Typography variant="body2" component="div">
            • Be specific about what data you want (e.g., "loan applications" vs "all data")
            <br />
            • Include time ranges when relevant (e.g., "last quarter", "this year")
            <br />
            • Mention specific filters (e.g., "pending status", "tech industry")
            <br />
            • Ask for comparisons or trends (e.g., "compare by industry", "trends over time")
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AIQueryAssistant;
